import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Onboarding from './Onboarding';

const ONBOARDING_KEY = 'ideaverdict.onboarded.v1';

/**
 * Native-shell glue layer for the Capacitor APK build.
 *
 * Responsibilities:
 *  - Hide the splash screen as soon as React has mounted (otherwise it stays
 *    for `launchShowDuration` and feels stuck).
 *  - Configure the Android status bar (emerald, light icons).
 *  - Show the first-launch onboarding (4 slides) ONCE per install.
 *  - Bind the Android hardware back button to the React Router stack so it
 *    behaves like a native back button instead of always closing the app.
 *
 * On the web build, all Capacitor calls are no-ops because we lazy-import
 * inside isNativePlatform() guards — so it ships in both targets.
 */
export default function NativeShell() {
  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    try {
      // Show onboarding only on the FIRST launch of the APK. On the web we
      // skip it entirely (web users are already past any "first-impression"
      // moment). The flag is persisted in localStorage which Capacitor mirrors
      // into native preferences automatically.
      const isNative = typeof window !== 'undefined' && (window as any)?.Capacitor?.isNativePlatform?.();
      if (!isNative) return false;
      return localStorage.getItem(ONBOARDING_KEY) !== '1';
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    (async () => {
      const isNative = typeof window !== 'undefined' && (window as any)?.Capacitor?.isNativePlatform?.();
      if (!isNative) return;

      // Lazy-load the Capacitor plugins so this file is safe to import on web.
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        // Brief delay so the splash transition feels intentional, not a flash.
        setTimeout(() => SplashScreen.hide().catch(() => undefined), 500);
      } catch { /* plugin not available — splash will auto-hide */ }

      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });   // light text/icons
        await StatusBar.setBackgroundColor({ color: '#10b981' });
        await StatusBar.setOverlaysWebView({ overlay: false });
      } catch { /* not available — system default */ }

      try {
        const { App } = await import('@capacitor/app');

        // Hardware back button: pop the router stack if there's history,
        // otherwise let the OS minimize the app (don't kill it).
        const backHandle = await App.addListener('backButton', () => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            App.minimizeApp().catch(() => undefined);
          }
        });

        // Deep-link bridge: when the OS hands us a `com.ideaverdict.app://…`
        // URL (typically the OAuth callback after Google/Apple sign-in),
        // strip the scheme + host and push the path+hash into React Router
        // so /auth/callback can finalize the session.
        const urlHandle = await App.addListener('appUrlOpen', (data: { url: string }) => {
          try {
            const parsed = new URL(data.url);
            // Path is everything after the host: e.g. /auth/callback
            // Hash carries the OAuth tokens: #access_token=…&refresh_token=…
            const target = (parsed.pathname || '/') + (parsed.search || '') + (parsed.hash || '');
            // Use window.location.replace so Supabase's detectSessionInUrl picks
            // up the hash on the next render — React Router's navigate() doesn't
            // expose the hash to supabase-js the same way.
            if (parsed.hash && parsed.pathname.includes('/auth/callback')) {
              window.location.replace(target);
            } else {
              navigate(target, { replace: true });
            }
          } catch (e) {
            console.warn('[NativeShell] could not parse deep link:', data?.url, e);
          }
        });

        cleanup = () => { backHandle.remove(); urlHandle.remove(); };
      } catch { /* App plugin missing */ }
    })();

    return () => { cleanup?.(); };
  }, [navigate]);

  if (!showOnboarding) return null;

  return (
    <Onboarding
      onComplete={() => {
        try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch { /* private mode */ }
        setShowOnboarding(false);
        // Push the user straight into the auth screen after onboarding so
        // they can sign in or create an account without a second tap.
        navigate('/auth');
      }}
    />
  );
}
